import React, { useEffect, useState } from "react";
import { Alert, Divider, Spin, Switch, Button, Popconfirm, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faServer, faCopy, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { formatUuidT } from "../../../flatbuffers/display";
// INTERFACES
import { FormularyType } from "../../../interfaces/Wizard.interface";
// COMPONENTS
import FormInputText from "../../components/form/FormInputText";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import GlobalAlert2 from "../../home/GlobalAlert2";
// REDUX
import {
  usePostServerMutation,
  usePutServerMutation,
  useLazyGetServerQuery,
  useDeleteServerMutation,
} from "../../../redux/servers/serverAPI";
import { useLazyGetOrganizationAndChildsQuery } from "../../../redux/organizations/organizationAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { validateIpAString } from "../../../utils/utils";
// CONTEXT
import { useWizard } from "../WizardContext";
// WIZARD UTILS
import { WizardStep, wizardStepsFrom } from "../utils/wizardInvalidation";
import { useWizardStepDraft } from "../utils/useWizardStepDraft";
// STORE
import { RootState } from "../../../app/store";
import WizardExportButton from "../components/WizardExportButton";
import { WizardOpGate, useCurrentWizardStepAccess } from "../WizardStepGate";
// SERVER IDENTITY AND SITE NETWORK
import ServerNetworkEditor, {
  emptySiteNetwork,
  siteNetworkHasContent,
  siteNetworkProblems,
} from "./ServerNetworkEditor";
import type { SiteNetworkForm } from "./ServerNetworkEditor";
import { MAC_PATTERN, asText, serverIdentityBody, siteNetworkFromApi } from "./serverIdentity";
// SMTP NOTIFICATIONS
import ServerSmtpForm, { SmtpForm, emptySmtpForm, smtpHasContent } from "./ServerSmtpForm";

interface StepServerProps {
  editServerId?: string;
  standalone?: boolean;
  onSaveSuccess?: () => void;
  onSaveError?: () => void;
  onCancel?: () => void;
}

export default function StepServer({
  editServerId,
  standalone = false,
  onSaveSuccess,
  onSaveError,
  onCancel,
}: StepServerProps = {}) {
  const [t] = useTranslation("global");
  const { theme, globalOrganizationId } = useSelector((state: RootState) => state.home);
  const {
    state: wizardState,
    setState: setWizardState,
    markStepCompleted,
    unmarkStepCompleted,
  } = useWizard();

  const isEditMode = !!wizardState.serverId;
  const access = useCurrentWizardStepAccess();

  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [serverName, setServerName] = useState("");
  const [serverIp, setServerIp] = useState("");
  const [serverActive, setServerActive] = useState(true);
  // The MAC is how an edge server finds this record. It is burned into the NIC
  // and printed on the chassis, so it is knowable before the hardware is
  // racked: recorded here, the box registers with the same value at first boot
  // and is handed this server's configuration.
  const [serverMac, setServerMac] = useState("");
  const [serverSerial, setServerSerial] = useState("");
  const [siteNetwork, setSiteNetwork] = useState<SiteNetworkForm>(emptySiteNetwork);
  // What the API last sent for this server. Held so a save can merge the form
  // over it: the request replaces the whole site_network and this editor
  // models only part of it, so rebuilding from form state alone would drop the
  // resolvers and most of a bond's settings on a save the operator believes
  // changed only an address.
  const [siteNetworkOriginal, setSiteNetworkOriginal] = useState<unknown>(null);
  // Offline email relay. Held in the form only for now: the request bodies do
  // not carry these fields until the endpoint accepts them.
  const [smtp, setSmtp] = useState<SmtpForm>(emptySmtpForm);
  const [saving, setSaving] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState("");

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [
    triggerPostServer,
    {
      isSuccess: isSuccessPost,
      isLoading: isLoadingPost,
      isError: isErrorPost,
      error: errorPost,
      data: dataPost,
    },
  ] = usePostServerMutation();

  const [triggerPutServer, { isError: isErrorPut, error: errorPut }] = usePutServerMutation();

  const [triggerGetServer] = useLazyGetServerQuery();
  const [triggerDeleteServer, { isLoading: isLoadingDelete }] = useDeleteServerMutation();
  const [triggerGetOrganization] = useLazyGetOrganizationAndChildsQuery();

  // ************************************************ */
  // FUNCTIONS ************************************* */
  const onChangeFormValue = (newValue: string, variableName: string) => {
    if (variableName === "name") setServerName(newValue);
    if (variableName === "ip") setServerIp(newValue);
    if (variableName === "mac_address") setServerMac(newValue);
    if (variableName === "serial_number") setServerSerial(newValue);
  };

  // A MAC is optional — plenty of servers are configured before anyone knows
  // which box will be racked — but a malformed one is worse than none: the
  // lookup is an exact match, so a typo means the edge registers and is told
  // no such server exists.
  const isMacValid = () => serverMac.trim() === "" || MAC_PATTERN.test(serverMac.trim());

  const networkProblems = siteNetworkProblems(siteNetwork);

  const isFormValid = () =>
    serverName.trim() !== "" &&
    serverIp.trim() !== "" &&
    validateIpAString(serverIp) &&
    isMacValid() &&
    networkProblems.length === 0 &&
    !!wizardState.organizationId;

  const handleSave = async () => {
    if (!isFormValid()) return;
    const token = await GETJwtToken();
    const body = {
      name: serverName,
      ip: serverIp,
      active: serverActive,
      org_id: wizardState.organizationId,
      fb_enabled: true,
      ...serverIdentityBody(serverMac, serverSerial, siteNetwork, siteNetworkOriginal),
    };
    triggerPostServer({ body, token });
  };

  const handleUpdate = async () => {
    if (!isFormValid() || !wizardState.serverId) return;
    setSaving(true);
    const token = await GETJwtToken();
    try {
      await triggerPutServer({
        body: {
          id: wizardState.serverId,
          name: serverName,
          ip: serverIp,
          active: serverActive,
          org_id: wizardState.organizationId,
          fb_enabled: true,
          ...serverIdentityBody(serverMac, serverSerial, siteNetwork, siteNetworkOriginal),
        },
        token,
      }).unwrap();
      markStepCompleted(1);
      if (standalone && onSaveSuccess) onSaveSuccess();
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : "Unknown error";
      setStepError(`Failed to update server: ${errMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteServer = async () => {
    if (!wizardState.serverId) return;
    const token = await GETJwtToken();
    try {
      await triggerDeleteServer({ id: wizardState.serverId, token }).unwrap();
      // Clear all downstream entity state. The server is the root of
      // every wizard configuration past Organization, so deleting it
      // wipes adapters, channels, banks, and lifts in one shot.
      setWizardState((prev) => ({
        ...prev,
        serverId: null,
        adapterIds: [],
        channelIds: [],
        bankIds: [],
        liftIdsByBank: {},
        liftNamesByBank: {},
      }));
      // Unmark every step that depended on the now-deleted server —
      // Server through Review, inclusive. Deliberately the SAME range
      // that `clearStaleServerErrorAndReroute` in WizardContext.tsx
      // computes today for the other "this server is gone" path (a
      // stale-server 404 detected elsewhere): one real-world event,
      // one range. This call site now derives it structurally from
      // `wizardStepsFrom` instead of a hand-maintained list — the list
      // that used to live here omitted Review and had already drifted.
      //
      // Review matters because SetupWizardPage reads `isStepCompleted`
      // generically for both the breadcrumb status and the forward-jump
      // guard: a stale Review checkmark shows a false "finish" and lets
      // the operator skip straight past everything this delete just
      // invalidated. It is not a publish gate — StepReview's
      // REQUIRED_STEPS excludes Review and was already correct.
      unmarkStepCompleted(...wizardStepsFrom(WizardStep.Server));
      setServerName("");
      setServerIp("");
      setServerActive(true);
      setServerMac("");
      setServerSerial("");
      setSiteNetwork(emptySiteNetwork());
      setSiteNetworkOriginal(null);
      setSmtp(emptySmtpForm());
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : "Unknown error";
      setStepError(`Failed to delete server: ${errMsg}`);
    }
  };

  // ************************************************ */
  // USE EFFECTS *********************************** */
  useEffect(() => {
    if (isSuccessPost && dataPost && dataPost.data) {
      const rawServerId = dataPost.data.id;
      // Normalize: FlatBuffer-decoded responses return UuidT objects for id fields.
      // WizardState.serverId must be a plain string.
      const serverId =
        typeof rawServerId === "string"
          ? rawServerId
          : formatUuidT(rawServerId as Parameters<typeof formatUuidT>[0]);
      setWizardState((prev) => ({ ...prev, serverId }));
      markStepCompleted(1);
      if (standalone && onSaveSuccess) onSaveSuccess();
    }
  }, [isSuccessPost, dataPost, standalone, onSaveSuccess]);

  useEffect(() => {
    if (isErrorPost && standalone && onSaveError) {
      onSaveError();
    }
  }, [isErrorPost, standalone, onSaveError]);

  // Hydrate form fields when resuming a draft wizard with an existing serverId
  useEffect(() => {
    if (!wizardState.serverId) return undefined;
    // Guarded the way the org-name effect below already is. Without it a slow
    // GET lands after the operator has started typing and replaces everything
    // — and the blast radius is now the whole network editor, not just a name.
    let cancelled = false;
    const fetchServer = async () => {
      const token = await GETJwtToken();
      try {
        const result = await triggerGetServer({
          idServer: wizardState.serverId,
          token,
        }).unwrap();
        if (cancelled) return;
        if (result && result.data) {
          setServerName(result.data.name || "");
          setServerIp(result.data.ip || "");
          setServerActive(result.data.active ?? true);
          setServerMac(asText(result.data.macAddress));
          setServerSerial(asText(result.data.serialNumber));
          setSiteNetwork(siteNetworkFromApi(result.data.siteNetwork));
          setSiteNetworkOriginal(result.data.siteNetwork ?? null);

          const rawOrgId = result.data.orgId ?? result.data.org?.id;
          if (rawOrgId) {
            const orgId =
              typeof rawOrgId === "string"
                ? rawOrgId
                : formatUuidT(rawOrgId as Parameters<typeof formatUuidT>[0]);
            setWizardState((prev) => ({ ...prev, organizationId: orgId }));
          }

          markStepCompleted(1);
        }
      } catch {
        if (!cancelled) setStepError("Failed to load server data — you may need to re-create it");
      }
    };
    fetchServer();

    return () => {
      cancelled = true;
    };
  }, [wizardState.serverId]);

  // Seed wizardState.serverId from editServerId prop when used in standalone edit mode
  useEffect(() => {
    if (editServerId) {
      setWizardState((prev) => ({ ...prev, serverId: editServerId }));
    }
  }, [editServerId, setWizardState]);

  useEffect(() => {
    if (!standalone || wizardState.organizationId || !globalOrganizationId) return;
    setWizardState((prev) => ({ ...prev, organizationId: globalOrganizationId }));
  }, [standalone, wizardState.organizationId, globalOrganizationId, setWizardState]);

  // Resolve the organization NAME for display. Wizard state only carries the
  // organizationId, so we look the name up directly by id via /orgs/{id} — the
  // same endpoint the wizard hydrator and Organization step use. A prior
  // implementation scanned the /orgs/light/ list for a matching id, but that
  // list omits freshly-created draft orgs, leaving this field blank right after
  // the operator completed (and validated) the Organization step.
  useEffect(() => {
    if (!wizardState.organizationId) {
      setOrganizationName("");
      return undefined;
    }
    // Guard against a stale response overwriting a newer one if organizationId
    // changes again before this request resolves.
    let cancelled = false;
    const fetchOrgName = async () => {
      const token = await GETJwtToken();
      try {
        const result = await triggerGetOrganization({
          orgId: wizardState.organizationId,
          token,
        }).unwrap();
        if (!cancelled) setOrganizationName(String(result?.data?.name ?? "").trim());
      } catch {
        if (!cancelled) setOrganizationName("");
      }
    };
    fetchOrgName();
    return () => {
      cancelled = true;
    };
  }, [wizardState.organizationId]);

  // ************************************************ */
  // DRAFT PERSISTENCE ****************************** */
  // Persist the operator's half-typed server creation form so a browser
  // crash or accidental navigation doesn't lose the name/IP they were
  // entering. We only enable the draft path when:
  //   (a) we're inside the wizard (not the standalone edge-management
  //       editor — that one has its own hydration via editServerId), and
  //   (b) no serverId exists yet — once the server is created, the
  //       `wizardState.serverId` hydrate effect above takes over and the
  //       draft is redundant.
  //
  // Verification: save only when at least one of the operator-typed
  // fields is non-empty. An empty-form snapshot would needlessly
  // overwrite a previously good draft if the operator opened the step
  // and immediately navigated away.
  // The network editor is the largest single input surface in the wizard, so
  // leaving it out of the draft meant navigating to another step and back
  // silently emptied it — while name and IP came back, so the form looked
  // intact. Saving from there would then create a server with no network and
  // report success.
  // The SMTP password is left out of the draft on purpose. Drafts are stored
  // server-side as plain JSON, so keeping it there would persist a credential
  // the operator never submitted. It is re-entered after a reload.
  useWizardStepDraft<{
    serverName: string;
    serverIp: string;
    serverActive: boolean;
    serverMac: string;
    serverSerial: string;
    siteNetwork: SiteNetworkForm;
    smtp: Omit<SmtpForm, "pass">;
  }>({
    orgId: wizardState.organizationId,
    step: WizardStep.Server,
    scopeId: "server",
    scopeType: "step",
    draft: {
      serverName,
      serverIp,
      serverActive,
      serverMac,
      serverSerial,
      siteNetwork,
      smtp: { host: smtp.host, port: smtp.port, user: smtp.user, from: smtp.from },
    },
    onRestore: (saved) => {
      if (typeof saved.serverName === "string" && saved.serverName.trim() !== "") {
        setServerName(saved.serverName);
      }
      if (typeof saved.serverIp === "string" && saved.serverIp.trim() !== "") {
        setServerIp(saved.serverIp);
      }
      if (typeof saved.serverActive === "boolean") setServerActive(saved.serverActive);
      if (typeof saved.serverMac === "string") setServerMac(saved.serverMac);
      if (typeof saved.serverSerial === "string") setServerSerial(saved.serverSerial);
      if (saved.siteNetwork && Array.isArray(saved.siteNetwork.nics)) {
        setSiteNetwork(saved.siteNetwork);
      }
      if (saved.smtp && typeof saved.smtp === "object") {
        const { host, port, user, from } = saved.smtp;
        setSmtp((prev) => ({
          ...prev,
          host: typeof host === "string" ? host : prev.host,
          port: typeof port === "number" ? port : prev.port,
          user: typeof user === "string" ? user : prev.user,
          from: typeof from === "string" ? from : prev.from,
        }));
      }
    },
    // A draft is worth saving when the operator has entered ANYTHING. The last
    // clause used to read `siteNetworkProblems(...).length >= 0`, which is true
    // for every array, so validate() always returned true and the hook's
    // "don't clobber a previously-valid draft with an empty snapshot" guard
    // never fired.
    validate: (snapshot) =>
      snapshot.serverName.trim() !== "" ||
      snapshot.serverIp.trim() !== "" ||
      snapshot.serverMac.trim() !== "" ||
      siteNetworkHasContent(snapshot.siteNetwork) ||
      smtpHasContent({ ...emptySmtpForm(), ...snapshot.smtp }),
    disabled: standalone || !!wizardState.serverId,
  });

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin spinning={isLoadingPost || isLoadingDelete || saving}>
      <div style={{ padding: "16px" }}>
        {stepError && (
          <Alert
            type="error"
            showIcon
            closable
            message={stepError}
            onClose={() => setStepError(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        {isErrorPost && (
          <Alert
            type="error"
            showIcon
            message="Failed to create server. Please try again."
            style={{ marginBottom: 16 }}
          />
        )}
        <DrawerRowDivider icon={faServer} title={t("general.server")} />
        <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
          {isEditMode && wizardState.serverId && (
            <div className="container" style={{ marginBottom: "15px" }}>
              <div className="row">
                <div className="col-12 col-md-12">
                  <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <FormInputText
                        inputValue={formatUuidT(wizardState.serverId)}
                        inputLabelName="Server ID"
                        inputOnChange={() => {}}
                        inputVariableName="id"
                        isRequired
                        disabledInput
                        formularyType={FormularyType.WIZARD}
                      />
                    </div>
                    <Button
                      type="default"
                      size="middle"
                      icon={<FontAwesomeIcon icon={faCopy} />}
                      onClick={() => {
                        const idString = formatUuidT(wizardState.serverId);
                        navigator.clipboard.writeText(idString);
                        message.success("Server ID copied to clipboard");
                      }}
                      style={{ marginBottom: "4px" }}
                    />
                  </div>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-12 col-md-12">
                  <FormInputText
                    inputValue={organizationName}
                    inputLabelName={t("general.organization")}
                    inputOnChange={() => {}}
                    inputVariableName="organizationName"
                    isRequired
                    disabledInput
                    formularyType={FormularyType.WIZARD}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="container">
            <div className="row">
              <div className="col-12 col-md-6">
                <FormInputText
                  inputValue={serverName}
                  inputLabelName={t("general.name")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="name"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              <div className="col-12 col-md-6">
                <FormInputText
                  inputValue={serverIp}
                  inputLabelName="IP Address"
                  inputOnChange={onChangeFormValue}
                  inputVariableName="ip"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12 col-md-6">
                <FormInputText
                  inputValue={serverMac}
                  inputLabelName="MAC Address"
                  inputOnChange={onChangeFormValue}
                  inputVariableName="mac_address"
                  isRequired={false}
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
                <span className="generalStyles__inputLabel" style={{ color: "#888" }}>
                  {isMacValid()
                    ? "The edge server sends this at first boot to claim its configuration. Leave blank if the hardware is not chosen yet."
                    : "Expected six hex pairs, e.g. 00:1b:44:11:3a:b7."}
                </span>
              </div>
              <div className="col-12 col-md-6">
                <FormInputText
                  inputValue={serverSerial}
                  inputLabelName="Serial Number"
                  inputOnChange={onChangeFormValue}
                  inputVariableName="serial_number"
                  isRequired={false}
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
                <span className="generalStyles__inputLabel" style={{ color: "#888" }}>
                  Chassis serial. Used as a fallback match when the MAC is unknown or the NIC has
                  been replaced.
                </span>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Switch
                    data-doc-label="Active"
                    checked={serverActive}
                    onChange={(checked) => setServerActive(checked)}
                  />
                  <span>{t("general.active")}</span>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 mt-3">
                <span className="generalStyles__inputLabel" style={{ color: "#888" }}>
                  FlatBuffer is always enabled. Channels are added on the Channels step.
                </span>
              </div>
            </div>
            <div className="row">
              <div className="col-12 mt-3">
                <ServerNetworkEditor value={siteNetwork} onChange={setSiteNetwork} />
              </div>
            </div>
          </div>
        </div>
        <DrawerRowDivider icon={faEnvelope} title="SMTP notifications" />
        <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
          <ServerSmtpForm value={smtp} onChange={setSmtp} />
        </div>
        <Divider />
        {/* SAVE / DELETE BUTTONS */}
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="generalStyles__flexCenter">
                {!standalone && (
                  <div className="generalStyles__mrFix">
                    <WizardExportButton stage={1} orgId={wizardState.organizationId} />
                  </div>
                )}
                {standalone && onCancel && (
                  <div className="generalStyles__mrFix">
                    <Button onClick={onCancel} size="large" style={{ minWidth: "180px" }}>
                      Cancel
                    </Button>
                  </div>
                )}
                <div className="generalStyles__mrFix">
                  <Button
                    type="primary"
                    onClick={isEditMode ? handleUpdate : handleSave}
                    disabled={!isFormValid() || (isEditMode ? !access.update : !access.create)}
                    loading={isLoadingPost || saving}
                    size="large"
                    style={{ minWidth: "180px" }}
                  >
                    {isEditMode ? "Update Server" : "Save Server"}
                  </Button>
                </div>
                {!standalone && wizardState.serverId && (
                  <div className="generalStyles__mrFix">
                    <WizardOpGate op="delete">
                      <Popconfirm
                        title="Delete this server?"
                        onConfirm={handleDeleteServer}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          danger
                          loading={isLoadingDelete}
                          size="large"
                          style={{ minWidth: "180px" }}
                        >
                          Delete Server
                        </Button>
                      </Popconfirm>
                    </WizardOpGate>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ALERTS */}
      <GlobalAlert2
        isError={isErrorPost}
        isSuccess={isSuccessPost}
        requestType="POST"
        error={errorPost}
        name="Server"
      />
      <GlobalAlert2
        isError={isErrorPut}
        isSuccess={false}
        requestType="PUT"
        error={errorPut}
        name="Server"
      />
    </Spin>
  );
}
